import { Key } from 'react'

import { useLocation } from 'react-router'

import {
  AutocompleteOwnerState,
  AutocompleteRenderOptionState,
  Box,
  GlobalStyles,
  Popper,
  PopperProps,
  autocompleteClasses,
  useMediaQuery,
} from '@mui/material'
import {
  Theme, alpha, styled, useTheme
} from '@mui/material/styles'

import CatIcon from '../components/Cat/CatIcon'
import TypeIcon from '../components/Type/TypeIcon'

import { Cats } from '../constants/config/categories'
import UI_ROUTES from '../constants/config/routeVars'
import { Types } from '../constants/config/types'

export const POPUP_MAX_WIDTH = 460

export const PUBLIC_COLOR = '#ffd700' // Is used on things that are still private, and can be published (private/shared)
export const SHARE_COLOR = 'green' // Is used on things that are still private, and can be shared (private)

export const POST_CARD_HEADER_MIN_HEIGHT = 160

export const DRAWER_WIDTH = 211

export const heroHeight = '25vh'
export const heroHeightLargeDesktop = '33vh'

export const headerHeight = 144
export const headerHeightLargeDesktop = headerHeight

export const topBarHeight = 64 // Check App.css if changing
export const bottomSpacing = 32
export const bottomBarHeight = topBarHeight

export const scrollbarWidth = 12
export const scrollbarWidthLarge = 18
export const scrollbarHeight = 9
export const scrollbarHeightLarge = 14

export const mainContainerMaxWidth = 'md'

export const bottomBarCutoff = 'md'

export const postBodyPadding = 1

export const defaultStyles = {
  transitionSlowest: '3s all',
  transitionSlower: '2s all',
  transitionSlow: '1s all',
  transitionSmooth: '0.8s all',
  transition: '0.6s all',
  transitionFast: '0.4s all',
  transitionFastest: '0.2s all',

  transitionBezierFast: '0.3s cubic-bezier(.47,1.64,.41,.8)',

  blurFilterRed: 'drop-shadow(1px 1px 12px red)',
}

export function getHeroDims(open: boolean): {
  height: string
  desktopHeight: string
} {
  if (open) {
    return {
      height: heroHeight,
      desktopHeight: heroHeightLargeDesktop,
    }
  } else {
    return {
      height: `calc(${heroHeight} /2)`,
      desktopHeight: `calc(${heroHeightLargeDesktop} /2)`,
    }
  }
}

export const useSearchBoxStyles = () => {
  const { palette } = useTheme()
  return {
    position: 'relative',
    borderRadius: '4px',
    backgroundColor: alpha(palette.text.secondary || 'pink', 0.15),
    '&:hover': {
      backgroundColor: alpha(palette.text.secondary || 'pink', 0.25),
    },
    // marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    sm: {
      // marginLeft: theme.spacing(3),
      width: 'auto',
    },
  }
}

export function getBodyHeight(desktop?: boolean) {
  const heightMinusBottomBar = topBarHeight + bottomBarHeight
  return `calc(100vh - ${desktop ? topBarHeight : heightMinusBottomBar}px)`
}
export const useContainerStyles = () => {
  const theme = useTheme()
  const { pathname } = useLocation()
  const hideTopBar = UI_ROUTES.hideAppNav.includes(pathname)
  return {
    rootSpacing: {
      paddingTop: hideTopBar ? 0 : `${topBarHeight}px`,
      paddingBottom: 0,
      minHeight: '100vh', // Fix blank background showing after @mui v6
      height: '100%', // Fix blank background showing after @mui v6
      [theme.breakpoints.down(bottomBarCutoff)]: {
        paddingBottom: `${bottomBarHeight}px`,
      },
    },
    root: {
      flexGrow: 1,
      flexDirection: 'column',
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      paddingTop: theme.spacing(3),
      paddingBottom: `${bottomSpacing}px`,
      [theme.breakpoints.down(bottomBarCutoff)]: {
        paddingBottom: `${bottomBarHeight}px`,
      },
    },
    rootScroll: {
      // position: 'absolute',
      flexGrow: 1,
      flexDirection: 'column',
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      overflowY: 'scroll',
      maxWidth: '100%',
      overflowX: 'hidden',
      maxHeight: `calc(100vh - ${topBarHeight}px)`,
      height: `calc(100vh - ${topBarHeight}px)`,
      paddingTop: theme.spacing(3),
      paddingBottom: `${bottomSpacing}px`,
      [theme.breakpoints.down(bottomBarCutoff)]: {
        paddingBottom: `${bottomBarHeight}px`,
      },
    },
    rootSpacingNoFlex: {
      overflowX: 'scroll',
      paddingBottom: `${bottomSpacing}px`,
      [theme.breakpoints.down(bottomBarCutoff)]: {
        paddingBottom: `${bottomBarHeight}px`,
      },
    },
    rootMargins: {
      flexGrow: 1,
      flexDirection: 'column',
      display: 'flex',
      width: '100%',
    },
    forcedHeight: {
      height: `calc(100vh - ${topBarHeight}px)`,
      [theme.breakpoints.down(bottomBarCutoff)]: {
        height: `calc(100vh - ${topBarHeight + bottomBarHeight}px)`,
      },
    },
  }
}
export const useBreakpoints = () => {
  const muiTheme = useTheme()

  // const matchesSmallest = useMediaQuery(muiTheme.breakpoints.down('sm'))
  // const matchesMobile = useMediaQuery(muiTheme.breakpoints.down('md'))
  // const matchesMedium = useMediaQuery(muiTheme.breakpoints.down('lg'))
  // const matchesLarge = useMediaQuery(muiTheme.breakpoints.down('xl'))
  // const matchesLargest = useMediaQuery(muiTheme.breakpoints.up('lg'))

  // const largerThanMobile = useMediaQuery(muiTheme.breakpoints.up('md'))
  // const largerThanMedium = useMediaQuery(muiTheme.breakpoints.up('lg'))
  // const largerThanLarge = useMediaQuery(muiTheme.breakpoints.up('xl'))

  const matchesSmallest = useMediaQuery(muiTheme.breakpoints.down('xs'))
  const matchesMobile = useMediaQuery(muiTheme.breakpoints.down('sm'))
  const matchesMedium = useMediaQuery(muiTheme.breakpoints.down('md'))
  const matchesLarge = useMediaQuery(muiTheme.breakpoints.down('lg'))
  const matchesLargest = useMediaQuery(muiTheme.breakpoints.up('xl'))

  const largerThanMobile = useMediaQuery(muiTheme.breakpoints.up('sm'))
  const largerThanMedium = useMediaQuery(muiTheme.breakpoints.up('md'))
  const largerThanLarge = useMediaQuery(muiTheme.breakpoints.up('lg'))

  return {
    matchesSmallest,
    matchesMobile,
    matchesMedium,
    matchesLarge,
    matchesLargest,
    largerThanMobile,
    largerThanMedium,
    largerThanLarge,
  }
}

export const useGridXs = (forceMobile?: boolean) => {
  const {
    matchesMobile, matchesMedium, matchesLarge, matchesLargest
  } =
    useBreakpoints()
  return matchesMobile || forceMobile
    ? 12
    : matchesMedium
      ? 6
      : matchesLarge
        ? 4
        : matchesLargest
          ? 2
          : 3
}

export const fabSpacing = {
  bottom: {
    xs: 16 + 56,
    sm: 16 + 56,
    md: 16,
    lg: 16,
  },
}

const scrollBarStyles = (theme: Theme) => {

  return {
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 3px var(--mui-palette-scrollbar-shadowColor)',
      backgroundColor: theme.vars?.palette.background.paper,
    },
    '&::-webkit-scrollbar': {
      width: scrollbarWidth,
      height: scrollbarHeight,
      backgroundColor: theme.vars?.palette.background.paper,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.vars?.palette.info.main,
    },
  }
}

const scrollBarStylesLarge = (theme: Theme) => {

  return {
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 3px var(--mui-palette-scrollbar-shadowColor)',
      backgroundColor: theme.vars?.palette.background.paper,
    },
    '&::-webkit-scrollbar': {
      width: scrollbarWidthLarge,
      height: scrollbarHeightLarge,
      backgroundColor: theme.vars?.palette.background.paper,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.vars?.palette.info.main,
    },
  }
}

export const inputGlobalStyles = (theme: Theme) =>
  (
    <GlobalStyles
      styles={{
        '#root, body': {
        // backgroundColor: theme.vars?.palette.background.paper,
        },
        '#root, html, body': {
          ...scrollBarStyles(theme),
        },
        div: {
          ...scrollBarStyles(theme),
        },
        textarea: {
          ...scrollBarStyles(theme),
        },
        ul: {
          ...scrollBarStyles(theme),
        },
        '#recent-tags-body': {
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
        a: {
          color: theme.vars?.palette.primary.light,
        },
      }}
    />
  )

export const iconFontSize = (size: 'flat' | 'tiny' | 'mini' | 'small' | 'medium' | 'large' | 'extra-large' | 'huge' | 'jumbo' | 'gigantic') => {
  switch (size) {
    case 'flat':
    case 'tiny':
      return '1.2rem'

    case 'mini':
    case 'small':
      return '26px'

    case 'medium':
      return '32px'

    case 'large':
      return '42px'

    case 'extra-large':
      return '48px'

    case 'huge':
      return '82px'

    case 'jumbo':
      return '112px'

    case 'gigantic':
      return '188px'

    default:
      return '38px'
  }
}


function CustomPopper(props: PopperProps) {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        {
          name: 'flip',
          enabled: true,
          options: {
            fallbackPlacements: ['top-start'],
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altBoundary: true,
            tether: true,
            boundary: 'viewport',
          },
        },
      ]}
    />
  )
}


export const BasicScrollPopper = styled(CustomPopper)(({ theme }) =>
  ({
    [`& .${autocompleteClasses.listbox}`]: {
    // boxSizing: 'border-box',
      maxHeight: 'inherit',
      zIndex: theme.zIndex.editorDropdown,
      ...scrollBarStyles(theme),
      '& ul': {
        padding: 0,
        margin: 0,
      },
    },
  }))

export const RenderOption = (
  props: React.HTMLAttributes<HTMLLIElement> & { key: string },
  option: string,
  state: AutocompleteRenderOptionState,
  ownerState: AutocompleteOwnerState<string, false, false, false, 'div'>,
) => {
  const { key, ...rest } = props
  return (
    <Box
      key={`key-${key}`}
      sx={{
        mx: 1,
        my: 0.3,
        [`&.${autocompleteClasses.option}`]: {
          px: 1,
          py: 0.3,
        },
      }}
      component="li"
      {...rest}
    >
      {option ? ownerState.getOptionLabel(option) : <em>None</em>}
    </Box>
  )
}

export const RenderTypeOption = (
  props: React.HTMLAttributes<HTMLLIElement> & { key: Key },
  option: string,
  state: AutocompleteRenderOptionState,
  ownerState: AutocompleteOwnerState<string, false, false, false, 'div'>,
) => {
  const { key, ...rest } = props
  return (
    <Box
      key={`key-${String(key)}`}
      sx={{
        mx: 1,
        my: 0.3,
        [`&.${autocompleteClasses.option}`]: {
          px: 1,
          py: 0.3,
        },
      }}
      component="li"
      {...rest}
    >
      <TypeIcon
        type={option as Types | ''}
        mini
        sx={{ mr: 1 }}
      />
      {option ? ownerState.getOptionLabel(option) : <em>None</em>}
    </Box>
  )
}

export const RenderCatOption = (
  props: React.HTMLAttributes<HTMLLIElement> & { key: Key },
  option: string,
  state: AutocompleteRenderOptionState,
  ownerState: AutocompleteOwnerState<string, false, false, false, 'div'>,
) => {
  const { key, ...rest } = props
  return (
    <Box
      key={`key-${String(key)}`}
      sx={{
        mx: 1,
        my: 0.3,
        [`&.${autocompleteClasses.option}`]: {
          px: 1,
          py: 0.3,
        },
      }}
      component="li"
      {...rest}
    >
      <CatIcon
        cat={option as Cats}
        mini
        sx={{ mr: 1 }}
      />
      {option ? ownerState.getOptionLabel(option) : <em>None</em>}
    </Box>
  )
}
